/**
 * AlertMatrixCard — Story 3.4 (Referenz: settings.html, Notifications-Tab)
 *
 * Matrix-Tabelle 19 Event-Typen x 4 Kanaele (E-Mail / Push / Webhook / Telegram,
 * KEIN Discord) gegen GET/PUT /api/v2/notifications/matrix.
 *
 * - Toggle je Zelle mit Dirty-State: nur geaenderte Zellen landen im Bulk-PUT
 *   (Upsert je (event_type, channel), siehe Contract).
 * - Kanal-Spalte deaktiviert, wenn der Kanal nicht verfuegbar ist
 *   (channel_status[ch].available === false, z.B. Telegram nicht verbunden).
 * - Premium-Gate: matrix.premium.editable === false rendert read-only + Hinweis.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import {
  useAlertMatrix,
  ALERT_CHANNEL_ORDER,
  type AlertMatrix,
  type AlertChannel,
  type AlertEventType,
  type AlertMatrixEvent,
  type AlertPreference,
} from './useAlertMatrix'
import { toast } from '../../components/Toast/useToast'
import { apiErrorMessage } from '../../lib/errors/apiErrorMessage'

/** Zellen-Key im Draft: `${event_type}|${channel}`. */
function cellKey(eventType: string, channel: string): string {
  return `${eventType}|${channel}`
}

/**
 * Welche Spalten die Matrix ueberhaupt zeigt.
 *
 * Design-Referenz (settings.html) und ACCEPTANCE.md fordern konstant 4
 * Spalten (Email/Push/Telegram/Webhook) — ein nicht verfuegbarer Kanal wird
 * per channel_status[ch].available + Warn-Icon markiert, NICHT als Spalte
 * entfernt (P1-Fund: reale BE-Antworten liefern `channels` z.B. ohne
 * Telegram, solange der Account nicht verbunden ist; das ist eine
 * Verfuegbarkeits-Aussage, keine Spalten-Auswahl).
 */
function visibleChannels(_matrix: AlertMatrix): AlertChannel[] {
  return [...ALERT_CHANNEL_ORDER]
}

/**
 * Ist die Zelle (Event x Kanal) technisch zustellbar?
 *
 * WICHTIG (Contract-Drift): das Backend liefert je Event NUR
 * `key/label_key/label/legacy_type/values/value_sources/unsupported_channels`
 * — ein Feld `ev.channels` gibt es real NICHT, obwohl die OpenAPI es als
 * pflichtig fuehrt. Es wird hier deshalb nur noch als optionaler Fallback
 * gelesen; die Matrix darf unter keinen Umstaenden am Rendern scheitern.
 *
 * Reihenfolge der Wahrheitsquellen:
 *   1. `unsupported_channels` (BE: UNSUPPORTED_EVENT_CHANNELS, z.B.
 *      speedtest_result/email) -> Zelle tot,
 *   2. `value_sources[ch] === 'unsupported'` (gleiche Information je Zelle),
 *   3. `channels` (falls ein Backend es doch liefert),
 *   4. `values` — das BE schreibt fuer JEDEN Kanal einen Wert, also gilt ein
 *      vorhandener Schluessel als unterstuetzt,
 *   5. sonst: Spalte gilt als unterstuetzt (keine stillen leeren Zeilen).
 */
function eventSupportsChannel(ev: AlertMatrixEvent, ch: AlertChannel): boolean {
  const unsupported = ev.unsupported_channels
  if (Array.isArray(unsupported) && unsupported.includes(ch)) return false
  if (ev.value_sources?.[ch] === 'unsupported') return false

  const declared = ev.channels as string[] | undefined
  if (Array.isArray(declared) && declared.length > 0) return declared.includes(ch)

  const values = ev.values as Record<string, boolean> | undefined
  if (values && typeof values === 'object') {
    const keys = Object.keys(values)
    if (keys.length > 0) return keys.includes(ch)
  }

  return true
}

/**
 * Dirty-Map ueber einen neuen Server-Stand legen: Eintraege, die der Server
 * jetzt selbst so liefert (oder deren Zelle es nicht mehr gibt), fallen raus —
 * alle uebrigen lokalen Aenderungen bleiben erhalten (F4).
 */
function pruneDraft(
  draft: Record<string, boolean>,
  matrix: AlertMatrix | null,
): Record<string, boolean> {
  const entries = Object.entries(draft)
  if (entries.length === 0) return draft
  if (!matrix) return draft

  const next: Record<string, boolean> = {}
  for (const [key, value] of entries) {
    const [eventType, channel] = key.split('|')
    const event = matrix.events.find((ev) => ev.key === eventType)
    if (!event) continue
    const serverValue = Boolean(event.values?.[channel as AlertChannel])
    if (serverValue !== value) next[key] = value
  }
  return next
}

type SwitchProps = {
  on: boolean
  disabled: boolean
  label: string
  testId: string
  onToggle: () => void
}

/**
 * Zellen-Kontrolle: Checkbox statt Toggle-Switch (Design-Referenz settings.html
 * Notifications-Tab nutzt `<input type="checkbox">`, kein Switch — Switches
 * bleiben binaeren Einstellungen wie im Display-Tab vorbehalten).
 */
function MatrixSwitch({ on, disabled, label, testId, onToggle }: SwitchProps) {
  return (
    <input
      type="checkbox"
      checked={on}
      aria-checked={on}
      aria-label={label}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      data-testid={testId}
      style={{
        width: 15,
        height: 15,
        accentColor: 'var(--primary)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onChange={() => {
        if (!disabled) onToggle()
      }}
    />
  )
}

export function AlertMatrixCard() {
  const { t } = useTranslation('notifications')
  const { t: tc } = useTranslation('common')
  const { matrix, isLoading, isError, refetch, saveMatrix, isSaving } = useAlertMatrix()

  // Dirty-State: nur Zellen, die vom Server-Wert abweichen.
  const [draft, setDraft] = useState<Record<string, boolean>>({})

  // F4 (Welle-4b): Ein neuer Server-Stand (Refetch/Invalidierung, z.B. nach dem
  // Speichern des Traffic-Schwellenwerts oder einer Telegram-Aktion) darf
  // ungespeicherte Zellen NICHT mehr verwerfen. Merge-Strategie: die Dirty-Map
  // bleibt ueber dem Refetch liegen; verworfen werden nur Eintraege, die der
  // Server inzwischen selbst so liefert (dann sind sie nicht mehr dirty).
  // Weiterhin adjust-during-render statt setState im Effect.
  const [prevMatrix, setPrevMatrix] = useState(matrix)
  if (matrix !== prevMatrix) {
    setPrevMatrix(matrix)
    setDraft((prev) => pruneDraft(prev, matrix))
  }

  if (isLoading) {
    return (
      <div className="card card-pad" data-testid="alert-matrix-card">
        <div className="skeleton" style={{ height: 220 }} />
      </div>
    )
  }

  if (isError || !matrix) {
    return (
      <div className="card card-pad" data-testid="alert-matrix-card">
        <h3 style={{ marginBottom: 10 }}>{t('matrix.title')}</h3>
        <p className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
          {t('matrix.loadError')}
        </p>
        <button type="button" className="btn btn-sm" onClick={() => refetch()}>
          {tc('actions.retry')}
        </button>
      </div>
    )
  }

  const channels = visibleChannels(matrix)
  const events = (Array.isArray(matrix.events) ? matrix.events : []).filter(
    (ev) => ev.visible !== false,
  )
  const readOnly = matrix.premium?.editable === false

  const channelAvailable = (ch: AlertChannel): boolean =>
    matrix.channel_status?.[ch]?.available !== false

  const channelReason = (ch: AlertChannel): string | undefined => {
    if (channelAvailable(ch)) return undefined
    const reason = matrix.channel_status?.[ch]?.reason
    return reason ? t(`matrix.reasons.${reason}`) : t('matrix.reasons.unavailable')
  }

  const serverValue = (ev: AlertMatrixEvent, ch: AlertChannel): boolean =>
    Boolean(ev.values?.[ch])

  const cellValue = (ev: AlertMatrixEvent, ch: AlertChannel): boolean =>
    draft[cellKey(ev.key, ch)] ?? serverValue(ev, ch)

  const toggleCell = (ev: AlertMatrixEvent, ch: AlertChannel) => {
    const key = cellKey(ev.key, ch)
    setDraft((prev) => {
      const next = { ...prev }
      const target = !(key in prev ? prev[key] : serverValue(ev, ch))
      if (target === serverValue(ev, ch)) {
        delete next[key]
      } else {
        next[key] = target
      }
      return next
    })
  }

  const eventLabel = (ev: AlertMatrixEvent): string => {
    const key = `matrix.events.${ev.key}`
    const translated = t(key)
    if (translated !== key) return translated
    return ev.label ?? ev.key
  }

  const channelLabel = (ch: AlertChannel): string => t(`matrix.channels.${ch}`)

  const dirtyCount = Object.keys(draft).length

  const handleSave = async () => {
    const preferences: AlertPreference[] = Object.entries(draft).map(([key, enabled]) => {
      const [event_type, channel] = key.split('|')
      return {
        event_type: event_type as AlertEventType,
        channel: channel as AlertChannel,
        enabled,
      }
    })
    if (preferences.length === 0) return
    try {
      await saveMatrix(preferences)
      // Gespeicherte Zellen aus dem Draft nehmen — inzwischen NEU getoggelte
      // Zellen (waehrend des Requests) bleiben erhalten (F4).
      const savedKeys = new Set(Object.keys(draft))
      setDraft((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([key]) => !savedKeys.has(key))),
      )
      toast.success(t('matrix.saveSuccess'))
    } catch (e) {
      toast.error(apiErrorMessage(e, t('matrix.saveError')))
    }
  }

  return (
    <div className="card card-pad" data-testid="alert-matrix-card">
      <div className="spread">
        <h3>{t('matrix.title')}</h3>
        <span className="pill" style={{ fontSize: 10 }} data-testid="alert-matrix-count">
          {t('matrix.eventCount', { count: events.length })}
        </span>
      </div>
      <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
        {t('matrix.subtitle')}
      </p>

      {readOnly && (
        <div
          className="alert alert-info"
          data-testid="alert-matrix-premium-hint"
          style={{
            margin: '12px 0 0',
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid var(--border)',
            fontSize: 13,
          }}
        >
          {t('matrix.premiumRequired')}{' '}
          <Link to="/plans">{t('components:premiumRequired.upgrade')}</Link>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="table" style={{ marginTop: 14, minWidth: 560, width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>{t('matrix.eventCol')}</th>
              {channels.map((ch) => {
                const available = channelAvailable(ch)
                const reason = channelReason(ch)
                return (
                  <th
                    key={ch}
                    data-testid={`alert-matrix-th-${ch}`}
                    title={reason}
                    style={{
                      textAlign: 'center',
                      color: available ? 'inherit' : 'var(--text-muted)',
                    }}
                  >
                    {channelLabel(ch)}
                    {!available && (
                      <span
                        role="img"
                        aria-label={reason}
                        data-testid={`alert-matrix-warn-${ch}`}
                        style={{ marginLeft: 4 }}
                      >
                        ⚠️
                      </span>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <tr key={ev.key} data-testid={`alert-matrix-row-${ev.key}`}>
                <td style={{ fontSize: 13 }}>{eventLabel(ev)}</td>
                {channels.map((ch) => {
                  const supported = eventSupportsChannel(ev, ch)
                  if (!supported) {
                    return (
                      <td
                        key={ch}
                        data-label={channelLabel(ch)}
                        data-testid={`alert-matrix-unsupported-${ev.key}-${ch}`}
                        title={t('matrix.reasons.unsupported')}
                        style={{ textAlign: 'center', color: 'var(--text-muted)' }}
                      >
                        <span aria-label={t('matrix.reasons.unsupported')} role="img">
                          —
                        </span>
                      </td>
                    )
                  }
                  const disabled = readOnly || !channelAvailable(ch)
                  return (
                    <td key={ch} data-label={channelLabel(ch)} style={{ textAlign: 'center' }}>
                      <MatrixSwitch
                        on={cellValue(ev, ch)}
                        disabled={disabled}
                        label={`${eventLabel(ev)} · ${channelLabel(ch)}`}
                        testId={`alert-matrix-toggle-${ev.key}-${ch}`}
                        onToggle={() => toggleCell(ev, ch)}
                      />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        className="row"
        style={{ gap: 10, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={handleSave}
          disabled={dirtyCount === 0 || isSaving || readOnly}
          data-testid="alert-matrix-save"
        >
          {isSaving ? t('matrix.saving') : tc('actions.save')}
        </button>
        {dirtyCount > 0 && (
          <>
            <span
              className="muted"
              style={{ fontSize: 12 }}
              role="status"
              aria-live="polite"
              data-testid="alert-matrix-dirty"
            >
              {t('matrix.dirtyHint', { count: dirtyCount })}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => setDraft({})}
              disabled={isSaving}
              data-testid="alert-matrix-discard"
            >
              {t('matrix.discard')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
